import React from 'react';
import {DialogContent, Stack, Typography, Link, IconButton, Box, Divider, InputAdornment} from "@mui/material";
import {
    Formik,
    Form,
    Field
} from 'formik';
import * as Yup from 'yup';
import {usePostPasswordResetRequestByUserEmailMutation} from '../../../redux/residentPortalApi'
import LoadingCover from '../../common/LoadingCover'
import {useAppDispatch} from "../../../redux/hooks";
import authSlice from "../../../redux/reducers/authSlice";
import { Close, LockReset, Email } from '@mui/icons-material';
import useIsMobile from '../../../hooks/useIsMobile';
import { StyledDialogTitle, IconContainer, ResetIcon, StyledTextField, StyledButton } from '../../common/styledComponents/AuthComponents';

type MyFormValues = {
    email: string
}

const ResetRequestFormSchema = Yup.object().shape({
    email: Yup.string()
        .required('Please enter your account email address'),
})

interface OwnProps {
    close: () => void;
}

const ResetRequestForm = (props: OwnProps) => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const [sendPasswordReset, {isLoading, isSuccess}] = usePostPasswordResetRequestByUserEmailMutation()
    const initialValues:MyFormValues = {
        email: ''
    };

    const handleSubmit = async (values: MyFormValues) => {
        const body = {userEmail: values.email}
        sendPasswordReset(body)
    }

    function openLogin() {
        dispatch(authSlice.actions.setAuthState('login'))
    }

    const successComponent = (
        <Stack spacing={4} alignItems="center" justifyContent="center" sx={{
            background: 'white', 
            mx: 5, 
            p: 4,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}>
            <Box sx={{ 
                backgroundColor: theme => theme.palette.success.light,
                borderRadius: '50%',
                width: 80,
                height: 80,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1
            }}>
                <LockReset sx={{ 
                    color: theme => theme.palette.success.dark, 
                    fontSize: 40 
                }} />
            </Box>
            
            <Typography 
                align="center" 
                variant="h5" 
                fontWeight="600"
                color="primary"
                sx={{ mb: 1 }}
            >
                Request Sent Successfully
            </Typography>
            
            <Box sx={{ 
                backgroundColor: theme => theme.palette.grey[50],
                borderRadius: '8px',
                p: 2,
                border: theme => `1px solid ${theme.palette.grey[200]}`
            }}>
                <Typography 
                    align="center" 
                    variant="body1"
                    sx={{ mb: 2 }}
                >
                    <strong>Your request has been received.</strong>
                </Typography>
                <Typography 
                    align="center" 
                    variant="body2"
                    color="text.secondary"
                >
                    If an email address matching your account is found, you will receive an email with instructions on how to reset your password.
                </Typography>
            </Box>

            <StyledButton 
                variant="contained" 
                onClick={openLogin} 
                data-cy="backToLoginButton"
                fullWidth
                size="large"
                sx={{
                    mt: 2,
                    backgroundColor: theme => theme.palette.primary.main,
                    '&:hover': {
                        backgroundColor: theme => theme.palette.primary.dark,
                    }
                }}
            >
                Return to Login
            </StyledButton>
        </Stack>
    )

    return (
        <React.Fragment>
            <StyledDialogTitle
                data-cy="dialogue-title" 
                sx={{
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center',
                    justifyContent: isMobile ? 'space-between' : 'center'
                }}
            >
                <Typography component="span" variant="h5" fontWeight="600" color={'white'}>
                    Reset Password
                </Typography>
                {
                    isMobile ?
                        <IconButton
                            onClick={props.close}
                            sx={{ color: 'white' }}
                        >
                            <Close
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: 24
                                }}
                            />
                        </IconButton> : null
                }
            </StyledDialogTitle>

            <DialogContent sx={{ padding: '24px', paddingBottom: '32px' }}>
                <IconContainer>
                    <ResetIcon>
                        <LockReset sx={{ color: 'white', fontSize: 28 }} />
                    </ResetIcon>
                </IconContainer>
                
                <Typography 
                    align="center" 
                    variant="h6" 
                    fontWeight="500" 
                    sx={{ marginBottom: '16px' }}
                >
                    Forgot Your Password?
                </Typography>
                
                <Typography 
                    align="center" 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ marginBottom: '24px' }}
                >
                    Enter your email address and we'll send you instructions to reset your password.
                </Typography>
                
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={ResetRequestFormSchema}
                >
                    {({errors}) => 
                        <Form>
                            <Stack spacing={4} width="100%" alignItems="center">
                                <Field
                                    as={StyledTextField}
                                    variant="outlined"
                                    fullWidth
                                    data-cy={'reset-request-email'}
                                    name={'email'}
                                    label={'Email address'}
                                    required={true}
                                    inputProps={{required: false}}
                                    type={'email'}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email ? errors.email : ''}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <StyledButton
                                    variant="contained"
                                    type="submit"
                                    fullWidth
                                    size="large"
                                    data-cy={"resetRequestSubmit"}
                                >
                                    Send Reset Instructions
                                </StyledButton>

                                <Divider sx={{ width: '100%', margin: '8px 0' }} />

                                <Typography align="center" variant="body2">
                                    <Link 
                                        onClick={openLogin}
                                        sx={{ 
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Back to login
                                    </Link>
                                </Typography>
                            </Stack>
                        </Form>
                    }
                </Formik>
            </DialogContent>
            {
                (isLoading || isSuccess) &&
                    <LoadingCover isSuccess={isSuccess} 
                        successComponent={successComponent}
                    />
            }
        </React.Fragment>
    )
}

export default ResetRequestForm;
